// Should contain API CALLS and API CALL RELATED methods only

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { ApiResponse } from '../models/api-response';
import { URLStore } from './endpoints';
import { Observable, throwError } from 'rxjs';
import { convertMapToJson } from './utilities';
import { EDITABLE_FIELD_IDS, MAX_LOCATION_BATCH } from '../components/locations-skeleton/constants';
import { ApiServiceAbstract, ResponseType } from './api-service-abstract';
import { FilterMap, SortMap } from '../models/locations-models/filter-map';
import { Location } from '../models/locations-models/location';
import { cloneDeep } from 'lodash';
import { ColumnNote } from '../models/locations-models/column-note';
import { CCMatrixRequestPayload, CCMatrixResponsePayload } from '../models/locations-models/cc-matrix-payload';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CC_OPERATIONS } from '../models/locations-models/cc-row-setting';
import { Coordinate } from '../models/coordinate';
import { Account } from '../models/account';
import { UserDetailsBeResponse } from '../models/users-models/user-details';
import { blsUsersResult } from '../models/users-models/bls-users-result';
import { blsUser } from '../models/users-models/blsUser';
import { EditUserPayload } from '../models/users-models/edit-user.payload';
import { UserPayload } from '../models/users-models/user.payload';
import { LocationLevel } from '../components/locations-skeleton/enums/location-level';
import { ReviewRequestParams } from '../models/review-request-params';
import { ReplyTemplate } from '../components/reviews/reply-tab-group/template-selection/template-selection.component';
import { DuplicateNameStrategy } from '../models/duplicate-name-strategy';
import { QARequestParams } from '../models/qa-request-params';
import { QAResponse } from '../models/qa-response';
import { Answer } from '../models/answer';
import { ReviewsResponse } from '../models/review-response';
import { TranslationItem } from '../components/global/filter-bar/filter-bar-models';
import { ReviewInsightsFilterInterface, TimeRangeBucketFormat } from '../models/review-insights-models/review-insights-filter';
import { LocationComparisonFilterInterface } from '../models/review-insights-models/location-comparison-filter';
import { InsightsMethod } from '../models/review-insights-models/insights-method';
import { generateDateRange } from '../models/date-range';
import { DefaultInsightPayload } from '../models/review-insights-models/default-insights-payload';
import { UpdateDifferencesPayload } from '../models/locations-models/update-differences-payload';
import { BatchModifyLocationGrpsPayload } from '../components/locations-skeleton/location-list/popups/batch-modify-location-grps-popup/batch-modify-location-grps-popup.component';
import { DifferenceTranslationRequestPayload, DifferenceTranslationResponsePayload } from '../models/locations-models/bls-gmb-difference';
import { GbpUpdateType } from '../components/locations-skeleton/location-list/popups/get-gbp-updates-popup/get-gbp-updates-popup.component';
import { LocationAddressTranslation } from '../models/locations-models/location-address-translation';
import { DatasourceSyncDetails } from '../models/datasource-sync-details';
import { BulkEditLocationGrpsResponse } from '../models/locations-models/bulk-edit-location-grps-response';
import { BulkUpdateDifferencesResponse } from '../models/locations-models/bulk-update-differences-response';
import { UpdateInGbpResponse } from '../models/locations-models/update-in-gbp-response';
import { Review } from '../models/review';
import { AiSettings, AiSettingsCreatePayload, AiSettingsReviewType, AiSettingsUpdatePayload, ReplyStyle } from '../components/reviews/reply-tab-group/ai-replies-settings/ai-replies-settings.component';
import { ReviewExportFormatChoice } from '../components/reviews/review-export-popup/review-export-popup.component';
import { CCLocation } from '../models/locations-models/cc-location';
import { ClaimUnclaimResponse } from '../models/claim-unclaim-response';
import { AiRepliesLanguage } from '../components/reviews/state/review.state';
import { BlsUpdate } from '../components/locations-skeleton/bls-updates/bls-updates-local-store.service';
import { AiFeedback, Feedback } from '../models/feedback';
import { QA } from '../models/qa';
import { EmailNotificationSettings } from '../models/email-notification-settings';
import { Campaign } from '../models/campaigns-models/campaign';
import { LocationData } from '../models/location-data';
import { CampaignPost, SocialMediaCampaignPost } from '../models/campaigns-models/campaign-post';
import { CampaignFilterPayload, CampaignFilters } from '../models/campaigns-models/campaign-filters';
import { ClientTokens } from '../models/campaigns-models/client-tokens';
import { CCSyncDetails } from '../models/locations-models/cc-sync-details';
import { AiAssistedReply } from '../models/ai-assisted-reply';
import { ChatRequest } from '../models/ai-insights/chat-request';
import { AgentResponse } from '../models/ai-insights/agent-response';
import { ChatSuggestions } from '../models/ai-insights/chat-suggestions';
import { ChatSessions } from '../models/ai-insights/chat-sessions';
import { ChatSessionDetail } from '../models/ai-insights/chat-session-detail';
import { ChatSession } from '../models/ai-insights/chat-session';
import { CoordinateHistory, CoordinateSource } from '../models/coordinate-history';
import { MyBAccount } from '../models/legacy-user';
import { UserRole } from '../components/users/enums/user-role-enum';
import { BulkValidationDetails, BulkValidationPayload } from '../models/bulk-validation-details';
import { MediaPlatformAccount, MediaPlatformType } from '../models/campaigns-models/media-platform-account';
import { PlatformValidationRules } from '../models/campaigns-models/platform-validation-rules';

@Injectable({
  providedIn: 'root',
})
export class ApiService extends ApiServiceAbstract {
  constructor(
      protected http: HttpClient,
  ) {
    super(http);
  }

  /************************************** API CALLS ***********************************************/

  /**
   * Send feedback
   * @param {Feedback} feedback
   */
  sendFeedback(feedback: Feedback): Observable<any> {
    const url = URLStore.feedback;

    return this.http.post(url, feedback, { withCredentials: true });
  }

  /**
   * Send AI feedback
   * @param {Feedback} feedback
   */
  sendAiFeedback(feedback: AiFeedback): Observable<any> {
    const url = URLStore.insights.feedback;

    return this.http.post(url, feedback);
  }

  /**
   * Exports BLS locations to Google sheets according to the applied filters (or all/selected locations if
   * no filters were applied).
   * @param accountId
   * @param fields
   * @param locationIds
   */
  exportLocations(accountId: number,
                  fields?: string[], // Only these location fields will be included in the export.
                  locationIds?: number[], // Only these locations will be included in the export.
  ): Observable<ApiResponse<string>> {
    const url = URLStore.location.exportLocations.replace('{accountId}', String(accountId));
    return this.post<ApiResponse>(url, { importedLocationIds: locationIds, sheetHeaders: fields });
  }

  getLocations(accountId: number, pageSize = MAX_LOCATION_BATCH, requestedPage?: number) {
    let url = URLStore.location.getLocations.replace('{accountId}', String(accountId));

    url += `?pageSize=${pageSize}`;

    if (requestedPage) {
      url += `&requestedPage=${requestedPage}`;
    }

    return this.get<ApiResponse<{ locations: Location[], nextPage: number }>>(url);
  }

  /**
   * Imports addresses via a CSV file. These locations are added to the current locations.
   * @param accountId ID of the account for which you want to import addresses
   * @param file CSV file that contains locations
   */
  importLocations(accountId: number, file: File): Observable<ApiResponse<Location[]>> {

    //check if the specified file is indeed a CSV file before sending
    if (!/.*\.csv$/.test(file.name)) {
      return throwError(new HttpErrorResponse({ error: { message: 'NO_CSV' } }));
    }

    const url = URLStore.location.uploadURL.replace(
        '{accountId}',
        String(accountId),
    );

    let formData: FormData = new FormData();
    formData.append('file', file);

    return this.post(url, formData);
  }

  /**
   * Exports duplicate locations to Google Sheets.
   * @param accountId ID of the account you want to export locations for
   */
  exportDuplicateLocations(accountId: number): Observable<ApiResponse<string>> {
    const url = URLStore.location.exportDuplicatesURL.replace(
        '{accountId}',
        String(accountId),
    );

    return this.get<ApiResponse<string>>(url);
  }


  /**
   * Exports starred C&C search results to Google Sheets.
   * @param accountId ID of the account you want to export starred C&C search results for
   * @param excludeLinkedResults Defines if already linked locations should be excluded from the search-result.
   */
  exportStarredCCSearchResults(accountId: number, excludeLinkedResults: boolean, importedLocationIds: number[] | null): Observable<ApiResponse<string>> {
    const url = URLStore.location.exportStarredCCSearchResultsURL.replace(
        '{accountId}',
        String(accountId),
    );

    let params = new HttpParams();
    params = params.set('excludeLinkedResults', String(excludeLinkedResults));

    const payload = { importedLocationIds: importedLocationIds };

    return this.http.post<ApiResponse<string>>(url, payload, {
      params: params,
      withCredentials: true,
    });
  }

  /**
   * Exports place names of a subset of locations based on desired languages as a CSV.
   * @param accountId ID of the account you want to export place names for
   * @param languageCodes List of desired languages' iso-codes
   * @param addressFilter Used to filter for locations for which to export place names.
   */
  exportPlaceNames(
      accountId: number,
      languageCodes: string[],
      addressFilter: FilterMap): Observable<ApiResponse<string>> {
    const url = URLStore.location.exportPlaceNamesURL.replace('{accountId}', String(accountId));

    let params = new HttpParams();
    params = params.set('languages', languageCodes.join(','));

    const payload = this.generateFilterPayload(addressFilter);

    return this.post(url, payload, {
      params: params,
    });
  }

  /**
   * Exports the differences of filtered locations.
   * @param accountId
   * @param filteredLocations locations which will be exported
   */
  exportDifferences(accountId: number, filteredLocations: Location[]): Observable<ApiResponse> {
    const url = URLStore.location.exportLocationDifferencesURL
        .replace('{accountId}', String(accountId));

    let payload = {
      importedLocationIds: filteredLocations.map(l => l.id),
      sheetHeaders: [],
    };

    return this.post<ApiResponse>(url, payload);
  }

  /**
   * Moves locations to the archive.
   * @param accountId ID of the account you want to archive locations for
   * @param locationIds Locations with these IDs will be moved.
   */
  archiveLocations(
      accountId: number,
      locationIds: number[],
  ): Observable<ApiResponse<Location[]>> {
    const url = URLStore.location.archiveURL.replace(
        '{accountId}',
        String(accountId),
    );

    let payload = {
      locationIds: locationIds,
    };

    return this.post<ApiResponse>(url, payload);
  }

  /**
   * Returns the BLS updates for a specific location (like a log file for BLS updates).
   * @param accountId ID of the account you want to get updates for
   * @param locationId Returns the updates for this location.
   */
  getLocationUpdates(
      accountId: number,
      locationId: number,
  ): Observable<ApiResponse<BlsUpdate[]>> {
    const url = URLStore.location.getUpdatesURL
        .replace('{accountId}', String(accountId))
        .replace('{locationId}', String(locationId));

    return this.get<ApiResponse<BlsUpdate[]>>(url);
  }

  /**
   * Sends an edited location to the backend, which replaces the old location with the edited location (only in BLS, not
   * in GMB).
   * @param accountId ID of the account you want to edit data for
   * @param editedLocation Old location with edited fields.
   */
  updateDataInBLS(
      accountId: number,
      editedLocation: Location,
  ): Observable<ApiResponse<Location>> {
    const url = URLStore.location.editInBlsURL
        .replace('{accountId}', String(accountId))
        .replace('{locationId}', String(editedLocation.id));

    let payload = this.generateEditLocationPayload(editedLocation);

    return this.put<ApiResponse<Location>>(url, payload);
  }


  /**
   * Bulk accept or unaccept differences between BLS and GMB values for multiple fields of a location.
   *
   * @param {number} accountId accountId ID of the account you want to edit data for
   * @param {number[]} locationIds An array with ID of the location for which changes should be accepted or unaccepted
   * @param {Map<string, boolean>} acceptFields Map with the fieldName as key and a boolean as the value. The boolean specifies is the difference should get accepted (true) or unaccepted (false).
   * @returns {Promise<ApiResponse>}
   */
  bulkEditDifferenceAcceptance(
      accountId: number,
      locationIds: number[],
      acceptFields: Map<string, boolean>,
  ): Observable<ApiResponse<BulkUpdateDifferencesResponse>> {
    const url = URLStore.location.bulkEditDifferenceAcceptanceURL
        .replace('{accountId}', String(accountId));


    let payload = {
      locationIds: locationIds,
      acceptFieldsMap: convertMapToJson(acceptFields),
    };

    return this.put<ApiResponse>(url, payload);
  }

  /**
   * Updates the differences (either accepting them or rejecting them)
   * AND patches the location GMB all in sequence. Fixes BLS-2482
   * @param accountId
   * @param locationId
   * @param payload
   *
   */
  updateDifferencesAndPatchGMB(
      accountId: number,
      locationId: number,
      payload: UpdateDifferencesPayload,
  ) {
    const url = URLStore.location.updateDifferencesAndPatchGMBLocationURL
        .replace('{accountId}', String(accountId))
        .replace('{locationId}', String(locationId));

    return this.put<ApiResponse<Location>>(url, payload);
  }

  /**
   * Updates the locations in Google Business Profile.
   * @param accountId ID of the account you want to update locations for
   * @param locationIds Locations with these IDs will be updated in GMB.
   * @param fieldMasks These fields of the locations will be updated.
   */
  updateLocationsInGbp(
      accountId: number,
      locationIds: number[],
      fieldMasks: string[],
  ): Observable<ApiResponse<UpdateInGbpResponse>> {

    const url = URLStore.location.patchGMBLocationsURL.replace(
        '{accountId}',
        String(accountId),
    );

    let payload = {
      locationIds: locationIds,
      fieldMasks: fieldMasks,
    };

    return this.post<ApiResponse>(url, payload);
  }

  /**
   * Blacklist/Whitelist a location's fields
   * @param accountId
   * @param locationIds
   * @param fieldsToBeBlacklisted
   * @param fieldsToBeWhitelisted
   */
  blacklistFields(
      accountId: number,
      locationIds: number[],
      fieldsToBeBlacklisted: string[],
      fieldsToBeWhitelisted: string[],
  ): Observable<ApiResponse<Location[]>> {
    const url = URLStore.location.blacklistFieldsURL.replace(
        '{accountId}',
        String(accountId),
    );

    let payload = {
      locationIds: locationIds,
      fieldsToBeBlacklisted: fieldsToBeBlacklisted,
      fieldsToBeWhitelisted: fieldsToBeWhitelisted,
    };

    return this.post<ApiResponse<Location[]>>(url, payload);
  }

  /**
   * Edit the connection between a BLS location and a GMB location.
   * @param accountId ID of the account you want to edit the connection for
   * @param locationId Connection will be edited for the location with this ID.
   * @param command 'create' or 'remove' a connection to a GMB location.
   */
  editGMBConnection(
      accountId: number,
      locationId: number,
      command: string,
  ): Observable<ApiResponse<Location>> {
    const url = URLStore.location.editGMBConnectionURL
        .replace('{accountId}', String(accountId))
        .replace('{locationId}', String(locationId))
        .replace('{command}', command);

    let params = new HttpParams();

    return this.put<ApiResponse<Location>>(url, {}, {
      withCredentials: true,
      params: params,
    });
    // 'create' can be a partial success, check response status (OK / WARNING)
  }

  /**
   * Refreshes BLS locations (get updates from GMB).
   * @param accountId ID of the account you want to refresh the connection for
   * @param locationIds Locations with these IDs will be refreshed.
   * @param dataToSync Types of data which should be refreshed.
   */
  refreshGMBConnection(
      accountId: number,
      locationIds: number[],
      dataToSync: GbpUpdateType[],
  ): Observable<ApiResponse<Location[]>> {
    const url = URLStore.location.refreshGMBConnectionURL.replace('{accountId}', String(accountId));
    return this.put(url, { locationIds, dataToSync });
  }

  /**
   * Bulk-edit location groups of 1 or several locations
   * @param accountId
   * @param payload
   */
  bulkEditLocationGroups(accountId: number, payload: BatchModifyLocationGrpsPayload): Observable<ApiResponse<BulkEditLocationGrpsResponse>> {
    const url = URLStore.location.bulkEditLocationGroupsURL.replace('{accountId}', String(accountId));
    return this.put<ApiResponse>(url, payload);
  }

  /**
   * Returns the original photo URL of a photo that has been edited / cropped.
   * @param editedUrl URL of the edited / cropped photo
   */
  getOriginalPhoto(editedUrl: string): Observable<string> {
    const url = URLStore.photo.getUrl;
    let payload = {
      editedURL: editedUrl,
    };
    return this.post(url, payload, {
      responseType: 'text',
      withCredentials: true,
    });
  }

  /**
   * Fields returned by call have an active data source connection
   * @param accountId
   */
  getDataSourceConfig(accountId: number): Observable<ApiResponse<Array<string>>> {
    const url = URLStore.location.dataSourceConfig.replace(
        '{accountId}', accountId.toString(),
    );

    return this.get<ApiResponse<Array<string>>>(url);
  }

  /**
   * Sync Reviews for the specified location
   * @param accountId
   * @param storeCode
   */
  syncLocationReviews(accountId: number, storeCode: string): Observable<ApiResponse> {
    let url = URLStore.review.syncLocationReviews;
    url = url.replace('{storeCode}', storeCode);

    return this.post<ApiResponse>(url, null, { headers: { 'X-Account-Id': accountId } });
  }

  /**
   * Gets Google translations for the diff popup
   * @param payload
   */
  getDifferenceTranslation(payload: DifferenceTranslationRequestPayload):
      Observable<ApiResponse<DifferenceTranslationResponsePayload>> {
    const url = URLStore.translate.difference;
    return this.post<ApiResponse<DifferenceTranslationResponsePayload>>(url, payload);
  }

  /**
   * Get all column notes for an account from the BE.
   * @param accountId
   */
  getColumnNotes(accountId: string): Observable<ApiResponse<ColumnNote[]>> {
    const url = URLStore.location.columnNotes.getAllURL.replace(
        '{accountId}',
        accountId);
    return this.get<ApiResponse<ColumnNote[]>>(url);
  }

  /**
   * Creates a column note and saves it to the backend, or updates it if it exists already.
   * @param accountId ID of the account where the columnNote gets saved
   * @param noteObject Column note that should be saved.
   */
  upsertColumnNote(accountId: string, noteObject: ColumnNote): Observable<ApiResponse<ColumnNote | undefined>> {
    let postURL = URLStore.location.columnNotes.postURL;
    let putURL = URLStore.location.columnNotes.putURL;

    // extract only the necessary params
    const payload = (({ columnName, noteText, noteColor }) => ({
      columnName,
      noteText,
      noteColor,
    }))(noteObject);

    // note doesn't have an ID so create it first thru PUT
    if (!noteObject.id) {
      postURL = postURL
          .replace(
              '{accountId}',
              accountId);
      return this.post<ApiResponse<ColumnNote>>(postURL, payload);
    } else {
      // update an existing note
      putURL = putURL
          .replace(
              '{accountId}',
              accountId)
          .replace('{columnNoteId}',
              noteObject.id.toString());
      return this.put<ApiResponse<ColumnNote>>(putURL, payload);
    }
  }

  /**
   * Deletes the columnNote in the backend.
   * @param accountId ID of the account where the columnNote gets saved
   * @param noteId ID of the columnNote that should be deleted.
   * @param columnName Name of the column for which the note should be deleted
   */
  deleteColumnNote(
      accountId: string,
      noteId: string,
      columnName: string): Observable<ApiResponse> {

    const url = URLStore.location.columnNotes.deleteURL
        .replace('{accountId}', accountId)
        .replace('{columnNoteId}', noteId);

    return this.delete<ApiResponse>(url);
  }

  /**
   * Retrieves a list of de-duplicated ISO-2 country codes used as dropdown options in the location
   * list country filter.
   */
  getCountryListISO2(accountId: number): Observable<ApiResponse<string[]>> {
    const url = URLStore.location.countrySet
        .replace('{accountId}', String(accountId));
    return this.get<ApiResponse<string[]>>(url);
  }

  /**
   * Retrieves a list of countries in which the signed-in user has GMB locations in ("missing"
   * locations excluded). Used in Rep mngt./Insights for country filter values
   */
  getGmbCountriesWithTranslations(accountId: number): Observable<TranslationItem[]> {
    const url = URLStore.review.countrySelectionList
        .replace('{accountId}', String(accountId));
    return this.post<ApiResponse<TranslationItem[]>>(url, {})
        .pipe(map(res => res?.result || []));
  }

  /**
   * Get the total number of managed locations and the number of closed locations.
   */
  getManagedLocationCount(accountId: string): Observable<ApiResponse> {
    const url = URLStore.location.getLocationCountsURL
        .replace(`{accountId}`, String(accountId));

    return this.get<ApiResponse>(url);
  }

  /**
   * Get the total number of locations for the logged in user.
   */
  getLocationsCount(accountId: string): Observable<ApiResponse> {
    const url = URLStore.location.getLocationsCountURL
        .replace(`{accountId}`, String(accountId));

    return this.get<ApiResponse>(url);
  }

  /**
   * Get the total number of questions for the logged in user.
   */
  getQuestionsCount(accountId: string): Observable<ApiResponse> {
    const url = URLStore.questions.countURL.replace('{accountId}', accountId);
    return this.get<ApiResponse>(url, {
      headers: { 'Content-Type': 'application/json', 'X-Account-Id': accountId },
    });
  }

  /**
   * Get CC Matrix. Will get mocked results if env var is 'true'
   * @param accountId
   * @param payload
   */
  getCCMatrix(accountId: string, payload: CCMatrixRequestPayload): Observable<ApiResponse<CCMatrixResponsePayload>> {
    const url = environment.activeFeatures.dev.claimAndCleanMocking ?
        URLStore.location.claimAndClean.ccMatrixURL.replace('{accountId}', accountId) + '?mock=true'
        :
        URLStore.location.claimAndClean.ccMatrixURL.replace('{accountId}', accountId);
    return this.post<ApiResponse<CCMatrixResponsePayload>>(url, payload);
  }

  /**
   * Gets the claiming target location group ID for the specified account.
   * @param accountId
   */
  getClaimingLocationGroupId(accountId: string): Observable<ApiResponse<string>> {
    const url = URLStore.location.claimAndClean.claimingTargetLocationGroup.replace(
        '{accountId}',
        String(accountId),
    );

    return this.get<ApiResponse<string>>(url);
  }

  /**
   * Sets the claiming target location group for the specified account.
   * @param accountId
   * @param claimingLocationGroupId
   */
  setClaimingLocationGroupId(
      accountId: number,
      claimingLocationGroupId: string): Observable<ApiResponse> {
    const url = URLStore.location.claimAndClean.claimingTargetLocationGroup.replace(
        '{accountId}',
        String(accountId),
    );

    return this.put<ApiResponse>(
        url, { claimingTargetLocationGroupId: claimingLocationGroupId });
  }

  /**
   * Gets the claim and clean keywords set on account-level.
   * @param accountId Gets the keywords for this account.
   */
  getClaimCleanKeywords(accountId: number): Observable<string[]> {
    const url = URLStore.location.claimAndClean.keywordsURL.replace('{accountId}', String(accountId));
    return this.get<ApiResponse>(url)
        .pipe(map(res => res.result));
  }

  /**
   * Sets the claim and clean keywords for the specified account.
   * @param accountId Sets the keywords for this account.
   * @param keywords Those keywords are set.
   */
  setClaimCleanKeywords(accountId: number, keywords: string[]): Observable<ApiResponse> {
    const url = URLStore.location.claimAndClean.keywordsURL
        .replace('{accountId}', String(accountId));
    return this.put<ApiResponse>(url, { keywordList: keywords });
  }

  /**
   * Gets the claim and clean radius set on account-level.
   * @param accountId Gets the radius for this account.
   */
  getClaimCleanRadius(accountId: number): Observable<number> {
    const url = URLStore.location.claimAndClean.radiusURL.replace('{accountId}', String(accountId));
    return this.get<ApiResponse>(url)
        .pipe(map(res => res.result));
  }

  /**
   * Sets the claim and clean radius for the specified account.
   * @param accountId Sets the radius for this account.
   * @param searchRadius
   */
  setClaimCleanRadius(accountId: number, searchRadius: number): Observable<any> {
    const url = URLStore.location.claimAndClean.radiusURL.replace('{accountId}', String(accountId));
    return this.put<ApiResponse>(url, { searchRadius: searchRadius });
  }

  /** Marks a location as either completed or uncompleted (undo cc completion). In the BE, this will
   * update the location's claimAndCleanCompetionTimestamp to the current time (if marked as
   * completed) or to null (if marked as uncompleted).
   * @param accountId
   * @param locationId Updates the timestamp for this location
   * @param shouldBeMarkedAsCompleted If the location will be marked as completed or uncompleted.
   * **/
  updateClaimCleanCompletion(accountId: number, locationId: number, shouldBeMarkedAsCompleted: boolean) {
    const url = URLStore.location.claimAndClean.markParentAsCompletedURL
        .replace('{accountId}', String(accountId))
        .replace('{locationId}', String(locationId));

    let params = new HttpParams();
    params = params.set('completed', String(shouldBeMarkedAsCompleted));

    const options = {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
      params: params,
    };
    return this.http.post<ApiResponse<Location>>(url, {}, options);
  }


  /**
   * request a claim and clean operation.
   * @param locationId
   * @param placeId
   * @param accountId
   * @param ccOperation
   * @param starredState
   */
  requestCcOperation(locationId: number,
                     placeId: string,
                     accountId: number,
                     ccOperation: CC_OPERATIONS,
                     starredState?: boolean): Observable<ApiResponse<CCLocation | ClaimUnclaimResponse | null>> {
    const url = this.generateCcOperationUrl(
        ccOperation, locationId, accountId);
    let params = new HttpParams();
    params = params.set('placeId', placeId);

    if (ccOperation === CC_OPERATIONS.CHANGE_STARRING) {
      params = params.set('starred', String(starredState));
    }

    const options = {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
      params: params,
    };

    return this.post(url, null, options);
  }

  /**
   * Constructs an http observable for coordinate validation
   * @param location
   * @param {Coordinate | null} coords If `null`, will perform INVALIDATION (BLS-2736).
   * @param source
   */
  verifyCoordinates(
      location: Location,
      coords: Coordinate | null,
      source: CoordinateSource,
  ): Observable<ApiResponse<Location>> {
    const url = URLStore.location.verifyCoordinates
        .replace('{accountId}', String(location.accountId))
        .replace('{locationId}', String(location.id));

    // a POST request cannot have a `null` body
    // so let's call DELETE instead - BLS-2736
    if (!coords) {
      return this
          .delete<ApiResponse>(url);
    }

    const params = new HttpParams().set('pinSource', source);

    return this
        .post<ApiResponse>(url, coords, { params });
  }

  /**
   * Schedule BULK validate / invalidate action
   * @param accountId
   * @param payload
   */
  bulkVerifyCoordinates(accountId: number, payload: BulkValidationPayload): Observable<ApiResponse> {
    const url = URLStore.location.bulkVerify
        .replace('{accountId}', String(accountId));

    return this.post(url, payload);
  }

  /**
   * Get BULK validate / invalidate action details
   * @param accountId
   */
  getBulkValidationDetails(accountId: number): Observable<ApiResponse<BulkValidationDetails>> {
    const url = URLStore.location.bulkVerifyStatus
        .replace('{accountId}', String(accountId));

    return this.get(url);
  }

  /**
   * Get historical location coordinates data
   * @param locationId
   */
  getCoordinateHistory(locationId: number): Observable<CoordinateHistory[]> {
    const url = URLStore.location.getCoordinateHistory
        .replace('{locationId}', String(locationId));
    return this.get(url);
  }

  /**
   * Get historical location coordinates data
   * @param locationId
   */
  getLastValidationSrc(locationId: number): Observable<CoordinateHistory> {
    const url = URLStore.location.getLastValidationSrc
        .replace('{locationId}', String(locationId));
    return this.get(url);
  }

  /**
   * Get account meta data
   * @deprecated in favor of getUserDetails
   */
  getBLSAccount(): Observable<Account> {
    const url = URLStore.blsAccounts.getURL;

    // For historical reasons, the `result` contains a 1-member list of accounts.
    // Prone to refactoring.
    return this.get<ApiResponse<Account[]>>(url)
        .pipe(
            map(({ result }) => result[0]),
        );
  }

  /**
   * Get GBP Groups
   * @param accountId
   */
  getGbpGroups(accountId: number): Observable<ApiResponse> {
    const url = URLStore.getGbpLocationGroups
        .replace('{accountId}', accountId.toString());

    return this.get(url);
  }

  /**
   * Get user details
   */
  getUserDetails(): Observable<ApiResponse<UserDetailsBeResponse>> {
    return this.get(URLStore.users.getUserDetails);
  }

  /**
   * Get users list
   */
  getBlsUsers(accountId: number): Observable<ApiResponse<blsUsersResult>> {
    const url = URLStore.users.getUsers.replace('{accountId}', accountId.toString());
    return this.get(url);
  }

  /**
   * Gets data for the create/edit user popup
   */
  getAccessibleUserRoles(): Observable<ApiResponse<UserRole[]>> {
    const url = URLStore.accessibleUserRoles;
    return this.get(url);
  }

  /**
   * Gets data for the create/edit user popup
   */
  getAccessibleLocationGroups(accountId: number): Observable<ApiResponse<MyBAccount[]>> {
    const url = URLStore.accessibleLocationGroups.replace('{accountId}', accountId.toString());
    return this.get(url);
  }

  /**
   * Create (invite) new user
   * @param accountId
   * @param payload
   */
  createUser(accountId: number, payload: UserPayload): Observable<ApiResponse<blsUser>> {
    const url = URLStore.users.createUser.replace('{accountId}', accountId.toString());
    return this.post(url, payload);
  }

  /**
   * Edit existing user
   * @param accountId
   * @param payload
   */
  editUser(accountId: number, payload: EditUserPayload): Observable<ApiResponse<blsUser>> {
    const url = URLStore.users.editOrDeleteUser
        .replace('{userId}', payload.userId)
        .replace('{accountId}', String(accountId));
    const payloadCopy = { ...payload };
    delete payloadCopy.userId;
    return this.put(url, payloadCopy);
  }

  /**
   * Delete existing user
   * @param userId
   * @param accountId
   */
  deleteUser(userId: number, accountId: number): Observable<ApiResponse> {
    const url = URLStore.users.editOrDeleteUser
        .replace('{userId}', String(userId))
        .replace('{accountId}', String(accountId));
    return this.delete(url);
  }

  /**
   * Resend invitation email to user
   * @param userId
   */
  resendInvitationEmail(userId: number): Observable<ApiResponse<blsUser>> {
    const url = URLStore.users.resendInvitation.replace('{userId}', String(userId));
    return this.post(url, null);
  }

  /**
   * Gets the GMB users according to the applied location level (single location level
   * or location group level).
   * @param accountId ID of the account for which you want to export users
   * @param locationLevel the difference between the group admins and the locations admins
   */
  getGbpUsers(accountId: number,
              locationLevel: LocationLevel): Observable<any> {
    const url = URLStore.users[locationLevel].replace('{accountId}', String(accountId));
    return this.get(url);
  }

  /**
   * Update user language in the DB
   * @param languageShort
   */
  changeUserLanguage(languageShort: string): Observable<ApiResponse> {
    const url = URLStore.users.changeLanguage;
    return this.post(url, { language: languageShort });
  }

  triggerLocalOAuthProcess(accountId: number): Observable<ApiResponse> {
    const url = URLStore.localOAuthURL.replace('{accountId}', String(accountId));
    const options = {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    };
    return this.http.get<ApiResponse>(url, options);
  }

  /**
   * Sends a request to the backend to fetch reviews. The parameters filter the reviews. Every parameter can be null if
   * you don't want to filter for it. If all the parameters are null, all reviews are fetched.
   * @param accountId
   * @param params
   */
  getReviews(accountId: number, params: ReviewRequestParams): Observable<ApiResponse<ReviewsResponse>> {
    const url = URLStore.review.getReviews;

    return this.post<ApiResponse<ReviewsResponse>>(url, params, {
      headers: { 'Content-Type': 'application/json', 'X-Account-Id': accountId },
    });

  }

  /**
   * Translate review text to the specified language (EN/DE). Will determine the source
   * language automatically.
   * @param accountId
   * @param reviewId
   * @param languageCode
   */
  translateReview(accountId: number,
                  reviewId: number,
                  languageCode: string): Observable<ApiResponse<{
    languageCode: string,
    reviewTranslatedText: string
  }>> {
    const url = URLStore.review.translate.replace('{reviewId}', String(reviewId));
    const headers = new HttpHeaders().set('X-Account-Id', String(accountId));

    return this.post(url, { languageCode }, { headers });
  }


  /**
   * Sends a request to the backend to fetch reviews in a specific format (e.g. Excel or CSV). The parameters filter the reviews.
   * If all reviews are to be fetched, req body should be null.
   * @param params
   * @param accountId
   * @param format
   */
  exportReviews(params: ReviewRequestParams | null, accountId: number, format: ReviewExportFormatChoice): Observable<any> {
    const url = URLStore.review.downloadReviews;
    const queryParams = new HttpParams().set('outputFormat', format);
    const headers = new HttpHeaders().set('X-Account-Id', String(accountId));
    headers.set('Content-Type', 'application/json');

    return this.http.post(url, params, {
      responseType: 'blob',
      headers,
      params: queryParams,
    });
  }

  /**
   * Saves, updates or deletes a reply to a review in the database and GMB.
   * @param reviewId ID of the review that you want to reply to
   * @param gbpLocationId
   * @param replyText Your reply text to the review
   * @param accountId
   * @param removeReply
   */
  answerReviewReply(
      reviewId: number,
      gbpLocationId: string,
      replyText: string,
      accountId: number,
      removeReply = false,
  ): Observable<ApiResponse<Review>> {
    if (!replyText) {
      replyText = '';
    }

    const url = URLStore.review.reply.replace(
        '{reviewId}',
        String(reviewId));

    let payload = {
      replyText: replyText.trim(),
      gbpLocationId,
    };

    return removeReply ? this.delete<ApiResponse>(url, { headers: { 'X-Account-Id': accountId } }) :
        this.put<ApiResponse>(url, payload, { headers: { 'X-Account-Id': accountId } });
  }

  /**
   * Reports ("flags") the review with this review ID.
   * @param reviewId ID of the review you want to report
   * @param accountId
   */
  reportReview(reviewId: number, accountId: number): Observable<ApiResponse<Review>> {
    const url = URLStore.review.flag.replace(
        '{reviewId}',
        String(reviewId),
    );
    return this.http.post<ApiResponse<Review>>(url, {}, { headers: { 'X-Account-Id': String(accountId) } });
  }

  /**
   * Returns a list of reply templates.
   * @param accountId
   */
  getReplyTemplates(accountId: number): Observable<ApiResponse<ReplyTemplate[]>> {
    const url = URLStore.review.templatesURL.replace('{accountId}', String(accountId));
    return this.get(url);
  }

  /**
   * Save a reply template. If the template has no ID yet, a new template will be created, otherwise
   * the existing template will be updated in Datastore.
   * @param template Template to save. Can be a new template (to create it) or an existing template
   * (to update it).
   * @param accountId
   */
  saveReplyTemplate(template: ReplyTemplate, accountId: number): Observable<ApiResponse<ReplyTemplate>> {
    const isNewTemplate: boolean = !template.id;

    const payload = {
      templateName: template.templateName,
      templateText: template.templateText,
    };

    let url = URLStore.review.templatesURL.replace('{accountId}', String(accountId));

    if (isNewTemplate) {
      return this.post(url, payload);
    } else {
      url = url.concat('/', String(template.id));
      return this.put(url, payload);
    }
  }

  /**
   * Deletes a reply template.
   * @param templateId Deletes the template with this ID.
   * @param accountId
   */
  deleteReplyTemplate(templateId: number, accountId: number): Observable<any> {
    const url = URLStore.review.templatesURL
        .replace('{accountId}', String(accountId)).concat('/', String(templateId));
    return this.delete(url);
  }

  /**
   * Uploads reply templates via a CSV file.
   * @param file CSV file that contains the reply templates
   * @param duplicateNameStrategy Strategy that defines what will happen if an uploaded reply
   * template has a name that already exists (has to be unique). If duplicateNameStrategy is NONE,
   * the BE will return an error in case of duplicate template names.
   * @param accountId
   */
  uploadReplyTemplates(file: File,
                       accountId: number,
                       duplicateNameStrategy: DuplicateNameStrategy = DuplicateNameStrategy.NONE): Observable<ApiResponse<ReplyTemplate[]>> {
    //check if the specified file is indeed a CSV file before sending
    if (!/.*\.csv$/.test(file.name)) {
      return throwError(() => new HttpErrorResponse({ error: { message: 'NO_CSV' } }));
    }

    const url = (URLStore.review.templatesURL + '/import').replace('{accountId}', String(accountId));
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('duplicateNameStrategy', duplicateNameStrategy);

    return this.post(url, formData);
  }

  /**
   * Exports all reply-templates as CSV.
   * @param accountId
   */
  exportReplyTemplates(accountId: number): Observable<any> {
    const url = (URLStore.review.templatesURL + '/export').replace('{accountId}', String(accountId));
    return this.http.post(url, {}, {
      responseType: 'blob',
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /**
   * Returns a list of AiSettings.
   * @param accountId
   */
  getAiSettings(accountId: number): Observable<ApiResponse<AiSettings[]>> {
    const url = URLStore.review.aiSettings.replace('{accountId}', String(accountId));
    return this.get(url);
  }

  /**
   * Returns a list of default languages for the AI settings' 'default language' dropdown.
   * @param accountId
   */
  getDefaultLanguages(accountId: number): Observable<ApiResponse<AiRepliesLanguage[]>> {
    const url = URLStore.review.defaultLanguage.replace('{accountId}', String(accountId));
    return this.get(url);
  }

  /**
   * Sets a default reply language for AI-generated replies. Responds with AiSettings of type 'LANGUAGE'
   * @param accountId
   * @param languageCode
   * @param reviewType
   */
  updateDefaultLanguage(accountId: number, languageCode: string, reviewType: AiSettingsReviewType): Observable<ApiResponse<AiSettings>> {
    const url = URLStore.review.defaultLanguage.replace('{accountId}', String(accountId));
    return this.put(url, { language: languageCode, reviewType: reviewType });
  }

  /**
   * Sets a default reply style for AI-generated replies. Responds with AiSettings of type 'STYLE'
   * @param accountId
   * @param replyStyle
   */
  updateReplyStyle(accountId: number, replyStyle: ReplyStyle): Observable<ApiResponse<AiSettings>> {
    const url = URLStore.review.replyStyle.replace('{accountId}', String(accountId));
    return this.put(url, { style: replyStyle });
  }

  /**
   * Saves AI settings for greetings and closings. If the setting has no ID,
   * a new setting will be created. Otherwise, the existing setting will be updated.
   * @param accountId The ID of the account for which to save the AI settings.
   * @param aiSettings The AI settings object to save.
   */
  saveAiSettings(accountId: number, aiSettings: AiSettings): Observable<ApiResponse<AiSettings>> {
    let url = URLStore.review.aiSettings.replace('{accountId}', String(accountId));
    const newGreetingClosing: boolean = !aiSettings.id;
    const options = {
      responseType: ResponseType.JSON,
      withCredentials: true,
    };
    if (newGreetingClosing) {
      const payload: AiSettingsCreatePayload = {
        type: aiSettings.type,
        title: aiSettings.title,
        text: aiSettings.text,
        selected: aiSettings.selected,
      };
      return this.post(url, payload, options);
    } else {
      const payload: AiSettingsUpdatePayload = {
        title: aiSettings.title,
        text: aiSettings.text,
        selected: aiSettings.selected,
      };
      url = url.concat('/', String(aiSettings.id));
      return this.put(url, payload, options);
    }
  }

  deleteAiSettings(accountId: number, aiSettingsId: number): Observable<ApiResponse<AiSettings>> {
    const url = URLStore.review.aiSettings.replace('{accountId}', String(accountId)).concat('/' + aiSettingsId);
    return this.delete(url);
  }

  /**
   * Sends a request to the backend to fetch questions and answers. The parameters filter the questions. Every parameter
   * can be null if you don't want to filter for it. If all of the parameters are null, all questions are fetched.
   * @param params
   * @param accountId
   */
  getQuestions(params: QARequestParams, accountId: number): Observable<ApiResponse<QAResponse>> {
    const url = URLStore.questions.getURL;
    return this.post(url, params, {
      headers: { 'Content-Type': 'application/json', 'X-Account-Id': accountId },
    });
  }

  /**
   * Sends a call to the backend to get a specific insight statistic (defined by the insightsMethod),
   * filtered by the given filters.
   * @param accountId
   * @param filters gets insights only for filtered locations/reviews
   * @param insightsMethod specifies which insights statistic we want to retrieve from the BE (e.g. response times)
   */
  getInsights(accountId: number,
              filters: ReviewInsightsFilterInterface | LocationComparisonFilterInterface,
              insightsMethod: InsightsMethod): Observable<ApiResponse> {
    const url = insightsMethod ? URLStore.insights.reviews
        .replace('{accountId}', String(accountId))
        .replace('{method}', insightsMethod) : null;
    if (!url) {
      return throwError('Backend route for the requested insights-type does not exist!');
    }

    let payload = this.generateInsightsPayload(filters, insightsMethod);

    return this.http.post<ApiResponse>(url, payload, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  }

  askAgent(chatRequest: ChatRequest): Observable<AgentResponse> {
    const url = URLStore.insights.chat;
    const options = {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    };


    return this.post(url, chatRequest, options);
  }

  getChatSuggestions(lang: string): Observable<ChatSuggestions> {
    const url = URLStore.insights.chatSuggestions;
    const queryParams = new HttpParams().set('lang', lang);

    return this.get(url, { params: queryParams });
  }

  wakeAgentInstance(): Observable<any> {
    const url = URLStore.insights.preflight;
    return this.post(url, {});
  }

  getChatSessions(page: number, pageSize: number): Observable<ChatSessions> {
    const url = URLStore.insights.sessions;
    const queryParams = new HttpParams()
        .set('page', page)
        .set('page_size', pageSize);

    return this.get(url, { params: queryParams });
  }

  getChatSessionDetail(id: string, page: number, pageSize: number): Observable<ChatSessionDetail> {
    const url = URLStore.insights.session.replace('{sessionId}', id);
    const queryParams = new HttpParams()
        .set('page', page)
        .set('page_size', pageSize);

    return this.get(url, { params: queryParams });
  }

  renameChatSession(id: string, newTitle: string): Observable<ChatSession> {
    const url = URLStore.insights.renameSession.replace('{sessionId}', id);
    return this.post(url, { new_title: newTitle });
  }

  deleteChatSession(id: string): Observable<any> {
    const url = URLStore.insights.deleteSession.replace('{sessionId}', id);
    return this.delete(url);
  }

  /**
   * Changes (or adds if one doesn't exist already) the answer to a question.
   * @param questionId Changes the answer to the question with this ID.
   * @param answerText Changes the answer to this text.
   * @param accountId
   */
  answerQuestion(questionId: number, answerText: string, accountId: number): Observable<ApiResponse<QA>> {
    const url = URLStore.questions.answerURL.replace(
        '{lscQuestionId}',
        String(questionId),
    );

    let payload = {
      answerText: answerText ? answerText.trim() : '',
    };

    return this.put(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        'X-Account-Id': accountId,
      },
    });
  }

  /**
   * Deletes an answer to a question.
   * @param answerId ID of the answer that should be deleted.
   * @param accountId
   */
  deleteAnswer(answerId: number, accountId: number): Observable<ApiResponse<Answer[]>> {
    const url = URLStore.questions.deleteAnswerURL.replace(
        '{answerId}',
        String(answerId),
    ).replace('{accountId}', String(accountId));

    return this.post<ApiResponse>(url, {});
  }

  /**
   * Gets translated address fields for a given LSC location
   * @param locationId
   */
  getLocationAddressTranslation(locationId: number): Observable<ApiResponse<LocationAddressTranslation>> {
    const url = URLStore.translate.locationAddress
        .replace('{locationId}', String(locationId));

    return this.get(url)
        // add id into response result
        .pipe(map((res: ApiResponse) => {
          if (res.result) {
            res.result = { id: locationId, ...res?.result };
          }

          return res;
        }));
  }

  /**
   * Get AI-assisted reply.
   * @param accountId
   * @param reviewId
   * @param selectedAiGreeting
   * @param selectedAiClosing
   * @param language
   * @param reviewType
   * @param replyStyle
   */
  getAiAssistedReply(accountId: number, reviewId: number, selectedAiGreeting: string | null,
                     selectedAiClosing: string | null, language: string, reviewType: AiSettingsReviewType,
                     replyStyle: ReplyStyle): Observable<ApiResponse<AiAssistedReply>> {
    const url = (URLStore.review.generateReply)
        .replace('{accountId}', String(accountId))
        .replace('{reviewId}', String(reviewId));

    let payload: {
      language: string;
      reply_language_stars_only: boolean;
      greeting?: string;
      closing?: string;
      style: ReplyStyle
    } = {
      language: language,
      reply_language_stars_only: reviewType === AiSettingsReviewType.STAR_ONLY,
      style: replyStyle,
    };
    if (selectedAiGreeting || selectedAiClosing) {
      payload = {
        ...payload,
        greeting: selectedAiGreeting,
        closing: selectedAiClosing,
      };
    }

    return this.post<ApiResponse>(url, payload ? payload : null);
  }

  /**
   * Call with each review reply when AI-assistance is active.
   * @param accountId
   * @param reviewId
   * @param replyText
   * @param aiAssistedReplyText
   * @param gbpLocationId
   * @param aiAssistedRequestId
   */
  replyAnalytics(accountId: number,
                 reviewId: number,
                 replyText: string,
                 aiAssistedReplyText: string,
                 gbpLocationId: string,
                 aiAssistedRequestId: string): Observable<ApiResponse> {
    const url = URLStore.review.analytics.replace('{reviewId}', String(reviewId));
    return this.put(url, {
      replyText,
      aiAssistedReplyText,
      gbpLocationId,
      aiAssistedRequestId,
    }, {
      headers: { 'Content-Type': 'application/json', 'X-Account-Id': accountId },
    });
  }

  /**
   * Get list of location IDs where {fieldId} was updated in the past 24h
   * @param accountId
   * @param fieldId
   * @param startDate // ISO time string
   * @param endDate // ISO time string
   */
  getLocationIdsWithUpdates(accountId: number, fieldId: string, startDate: string, endDate: string): Observable<ApiResponse<string[]>> {
    const url = URLStore.location.getLocationIdsWithUpdates
        .replace('{accountId}', String(accountId));

    let params = new HttpParams();
    params = params.set('importedLocationFieldName', fieldId);
    params = params.set('startDate', startDate);
    params = params.set('endDate', endDate);

    return this.get(url, { params });
  }

  /**
   * Get datasource sync details
   * @param accountId
   */
  getDatasourceSyncDetails(accountId: number): Observable<ApiResponse<DatasourceSyncDetails>> {
    const url = URLStore.location.datasourceSyncDetails
        .replace('{accountId}', String(accountId));

    return this.get(url);
  }

  /**
   * Schedule a datasource sync restart
   * @param accountId
   */
  restartDatasourceSync(accountId: number): Observable<ApiResponse> {
    const url = URLStore.location.restartDatasourceSync
        .replace('{accountId}', String(accountId));

    return this.post(url, null);
  }

  /**
   * Get email notification settings
   */
  getEmailNotificationSettings(): Observable<ApiResponse<EmailNotificationSettings>> {
    return this.get(URLStore.emailNotificationSettings);
  }

  /**
   * @param notificationsDailyEnabled
   */
  setEmailNotificationSettings(notificationsDailyEnabled: boolean): Observable<ApiResponse<EmailNotificationSettings>> {
    return this.put(URLStore.emailNotificationSettings, { notificationsDailyEnabled });
  }

  /**
   * Get datasource sync details
   * @param accountId
   */
  getCCSyncDetails(accountId: number): Observable<ApiResponse<CCSyncDetails>> {
    const url = URLStore.location.getPlacesApiSyncStatus
        .replace('{accountId}', String(accountId));

    return this.get(url);
  }

  /**
   * Schedule a datasource sync restart
   * @param accountId
   */
  restartCCSync(accountId: number): Observable<ApiResponse<CCSyncDetails>> {
    const url = URLStore.location.syncPlacesApi
        .replace('{accountId}', String(accountId));

    return this.put(url, null);
  }

  /**
   * Get campaign
   * @param accountId
   * @param filterPayload
   */
  getCampaigns(accountId: number, filterPayload: CampaignFilters): Observable<ApiResponse> {
    const url = URLStore.campaigns.getCampaigns;

    const payload: CampaignFilterPayload = {
      ...filterPayload,
      // User cannot change this ATM
      // sortFilterByDateTime: SortFilterByDateTime.updatedDateTime,
    };
    delete payload['title'];

    const options = {
      headers: { 'X-Account-Id': accountId },
      responseType: ResponseType.JSON,
      withCredentials: true,
    };

    return this.post<ApiResponse<Campaign[]>>(url, payload, options);
  }

  /**
   * Create / Update a campaign
   * @param accountId
   * @param campaign
   * @param files
   * @param id
   */
  upsertCampaign(accountId: number, campaign: Object, files: File[], id?: string): Observable<ApiResponse<Campaign>> {
    const url = id ?
        URLStore.campaigns.editCampaign.replace('{campaignId}', id) :
        URLStore.campaigns.createCampaign;
    const payload = new FormData();
    const options = {
      headers: { 'X-Account-Id': accountId },
    };

    payload.append('campaign', JSON.stringify(campaign));
    files.forEach(file => payload.append('files', file, file.name));

    return this.post<ApiResponse<Campaign>>(url, payload, options);
  }

  /**
   * Delete a campaign
   * @param accountId
   * @param campaignId
   */
  deleteCampaign(accountId: number, campaignId: string): Observable<ApiResponse<any>> {
    const url = URLStore.campaigns.deleteCampaign.replace('{campaignId}', campaignId);
    const options = {
      headers: { 'X-Account-Id': accountId },
    };

    return this.delete<ApiResponse>(url, options);
  }


  /**
   * Archive a campaign
   * @param accountId
   * @param campaignId
   */
  archiveCampaign(accountId: number, campaignId: string): Observable<ApiResponse<Campaign>> {
    const url = URLStore.campaigns.archiveCampaign.replace('{campaignId}', campaignId);
    const options = {
      headers: { 'X-Account-Id': accountId },
    };

    return this.put<ApiResponse>(url, null, options);
  }

  /**
   * Get location data.
   * @param accountId
   */
  getLocationData(accountId: number): Observable<ApiResponse<LocationData[]>> {
    const url = URLStore.accessibleLocations;
    const options = {
      headers: { 'X-Account-Id': accountId },
    };

    return this.get<ApiResponse>(url, options);
  }

  /**
   * Get campaign posts.
   * @param accountId
   * @param campaignId
   */
  getCampaignPosts(accountId: number, campaignId: string): Observable<ApiResponse<CampaignPost[]>> {
    const url = URLStore.campaigns.getPosts.replace('{campaignId}', campaignId);
    const options = {
      headers: { 'X-Account-Id': accountId },
    };

    return this.get(url, options);
  }

  getSocialMediaCampaignPosts(accountId: number, campaignId: string): Observable<ApiResponse<{
    instagram: SocialMediaCampaignPost[],
    facebook: SocialMediaCampaignPost[],
    linkedIn: SocialMediaCampaignPost[],
    x: SocialMediaCampaignPost[]
  }>> {
    const url = URLStore.campaigns.getSocialMediaPosts.replace('{campaignId}', campaignId);
    const options = {
      headers: { 'X-Account-Id': accountId },
    };

    return this.get<ApiResponse>(url, options);
  }

  /**
   * Get client tokens.
   * @param accountId
   */
  getClientTokens(accountId: number): Observable<ApiResponse<ClientTokens>> {
    const url = URLStore.campaigns.getClientTokens;
    const options = {
      headers: { 'X-Account-Id': accountId },
    };

    return this.get<ApiResponse>(url, options);
  }

  /**
   * Set client tokens
   * @param accountId
   * @param tokensToAdd
   */
  setClientTokens(accountId: number, tokensToAdd: number): Observable<ApiResponse<ClientTokens>> {
    const url = URLStore.campaigns.setClientTokens;
    const options = {
      headers: { 'X-Account-Id': accountId },
    };

    return this.post<ApiResponse>(url, { tokensToAdd }, options);
  }

  /**
   * Get connected platforms.
   * @param accountId
   */
  getPlatforms(accountId: number): Observable<ApiResponse<MediaPlatformAccount[]>> {
    const url = URLStore.campaigns.getPlatforms;
    const options = {
      headers: { 'X-Account-Id': accountId },
    };

    return this.get(url, options);
  }

  /**
   * Connect a new social media platform.
   * @param accountId
   * @param platform
   * @param frontendRedirectUrl
   */
  connectPlatform(accountId: number,
                  platform: MediaPlatformType,
                  frontendRedirectUrl: string): Observable<ApiResponse<{ redirectUrl: string }>> {
    const url = URLStore.campaigns.connectPlatform;
    const payload = {
      platform,
      frontendRedirectUrl,
    };
    const options = {
      headers: { 'X-Account-Id': accountId },
    };

    return this.post(url, payload, options);
  }

  /**
   * Disconnect a social media platform.
   * @param id
   * @param accountId
   */
  disconnectPlatform(id: string, accountId: number): Observable<ApiResponse<Partial<MediaPlatformAccount>>> {
    const url = URLStore.campaigns.disconnectPlatform.replace('{id}', id);
    const options = {
      headers: { 'X-Account-Id': accountId },
    };

    return this.delete(url, options);
  }

  /**
   * Get validation rules for supported platforms.
   * @param accountId
   */
  getValidationRules(accountId: number): Observable<ApiResponse<PlatformValidationRules[]>> {
    const url = URLStore.campaigns.getValidationRules;
    const options = {
      headers: { 'X-Account-Id': String(accountId) },
    };

    return this.get<ApiResponse<PlatformValidationRules[]>>(url, options);
  }

  getJasonerLink(): Observable<ApiResponse<string>> {
    const url = URLStore.jasoner;
    return this.get(url);
  }


  /****************************** API CALL HELPERS ONLY! *****************************************/

  // TODO: move to separate utils file

  /**
   * Generates filter payload.
   * @param filterMap Since AgGrid, will only contain { storeCode: string[] }.
   * @param sortMap Dictionary that contains instructions for sorting the locations, e.g. <'country', 'ASC'>
   */
  private generateFilterPayload(filterMap: FilterMap, sortMap?: SortMap) {
    // cloneDeep coz params can come straight out of the store and are immutable
    const payload = cloneDeep(filterMap) as {};

    if (sortMap) {
      payload['sortMap'] = sortMap;
    }

    return payload;
  }

  /**
   * Reformats a Location with just the fields that are needed in the backend.
   * @param editedLocation
   */
  private generateEditLocationPayload(editedLocation: Location): Object {
    //if a field is not passed that is expected by the backend, it may delete data!

    let jsonObject = {};
    EDITABLE_FIELD_IDS.forEach((field) => {
      jsonObject[field] = editedLocation[field];
    });
    return jsonObject;
  }

  /**
   * Generate a CC operation URL
   * @param ccOperation
   * @param locationId
   * @param accountId
   * @private
   */
  private generateCcOperationUrl(ccOperation: CC_OPERATIONS, locationId: number, accountId: number) {
    const claimUrl = URLStore.location.claimAndClean.claimGoogleMapsMatchURL;
    const undoClaimUrl = URLStore.location.claimAndClean.undoClaimAndLinkGoogleMapsMatch;
    const markAsDuplicateUrl = URLStore.location.claimAndClean.claimGoogleMapsMatchAsDuplicateURL;
    const undoMarkAsDuplicateUrl = URLStore.location.claimAndClean.undoClaimGoogleMapsMatchAsDuplicate;
    const changeStarringUrl = URLStore.location.claimAndClean.starGoogleMapsMatchURL;

    const generate = (urlWithPlaceholders: string): string => {
      return urlWithPlaceholders.replace('{accountId}', String(accountId))
          .replace('{locationId}', String(locationId));
    };

    switch (ccOperation) {
      case CC_OPERATIONS.CLAIM:
        return generate(claimUrl);
      case CC_OPERATIONS.UNDO_CLAIM:
        return generate(undoClaimUrl);
      case CC_OPERATIONS.MARK_AS_DUPE:
        return generate(markAsDuplicateUrl);
      case CC_OPERATIONS.UNDO_MARK_AS_DUPE:
        return generate(undoMarkAsDuplicateUrl);
      case CC_OPERATIONS.CHANGE_STARRING:
        return generate(changeStarringUrl);
    }
  }


  /**
   * Generates the payload for an API request according to the parameters. Parameters can be null.
   */
  private generateInsightsPayload(filters: ReviewInsightsFilterInterface | LocationComparisonFilterInterface,
                                  insightsMethod: InsightsMethod): DefaultInsightPayload {
    // default payload
    let payload: DefaultInsightPayload = {
      storeCodes: filters?.storeCode || null,
      businessNames: filters?.businessName || null,
      cities: filters?.city || null,
      countryCodes: filters?.countryCodes || null,
      bucketFormat: filters?.bucketFormat || TimeRangeBucketFormat.MONTH,
    };

    if (insightsMethod === InsightsMethod.LOCATION_COMPARISON_LIST) {
      // add table settings payload for locationComparison
      payload = {
        ...payload,
        pagesize: (filters as LocationComparisonFilterInterface).pageSize,
        pageoffset: (filters as LocationComparisonFilterInterface).pageSize *
            (filters as LocationComparisonFilterInterface).pageIndex,
        sortfield: (filters as LocationComparisonFilterInterface).sortField,
        sortdirection: (filters as LocationComparisonFilterInterface).sortDirection,
      };
    }

    if (insightsMethod !== InsightsMethod.LOCATION_COMPARISON_LIST &&
        insightsMethod !== InsightsMethod.LOCATION_COMPARISON_COUNT) {
      // add time filtering to payload for all endpoints except locationComparison
      payload = {
        ...payload,
        dateRange: generateDateRange(filters?.fromDate || null, filters?.toDate || null),
        days: filters?.days ? filters.days : null, // review numbers intervals
      };
    }

    return payload;
  }


}


